import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/jianpu.css";
import { getStarterSongs } from "../lib/jianpuSongs";
import { listSavedSongs, deleteSong } from "../lib/jianpuStorage";

// Jianpu Studio hub — a practice & composition studio for the EasyPlay 1s,
// built around numbered (jianpu) notation. Full-screen sub-site: the global
// navbar is hidden for /jianpu routes in App.js.

const JianpuStudio = () => {
  const [saved, setSaved] = useState([]);
  useEffect(() => {
    setSaved(listSavedSongs());
  }, []);

  const starters = getStarterSongs();

  const removeSaved = (id) => {
    deleteSong(id);
    setSaved(listSavedSongs());
  };

  return (
    <div className="jp-root">
      <div className="jp-shell">
        <Link to="/sillyprojects" className="jp-back">
          ← silly projects
        </Link>

        <header style={{ marginTop: "1.5rem", maxWidth: "38rem" }}>
          <p className="jp-kicker">numbered notation · movable do</p>
          <h1 style={{ fontSize: "2.4rem", marginTop: "0.4rem" }}>
            <span className="jp-hanzi">简谱</span> Jianpu Studio
          </h1>
          <p style={{ marginTop: "0.9rem", color: "var(--jp-ink-dim)" }}>
            A practice room for my EasyPlay 1s — fifteen big buttons, two
            octaves of a major scale, and a transpose dial so button ① is
            always <em>do</em>. Songs live in jianpu, the numbered notation
            where 1–7 are scale degrees, so nothing here cares what key the
            dial says. Learn a song with play-along scoring, or compose by
            typing (or just playing) and print your own tabs.
          </p>
        </header>

        <section style={{ marginTop: "2.5rem" }}>
          <h2 style={{ fontSize: "1.3rem" }}>Songbook</h2>
          <p style={{ color: "var(--jp-ink-dim)", fontSize: "0.9rem", marginTop: "0.2rem" }}>
            Starter tunes in the public domain — pick one, plug in, play along.
          </p>
          <div className="jp-card-grid" style={{ marginTop: "1rem" }}>
            {starters.map(({ id, song }) => (
              <Link key={id} to={`/jianpu/play/${id}`} className="jp-card">
                <span className="jp-card-title">{song.title}</span>
                <span className="jp-card-meta">
                  1={song.key} · {song.time.num}/{song.time.den} · ♩={song.tempo} ·{" "}
                  {song.barCount} bars
                </span>
              </Link>
            ))}
          </div>
        </section>

        {saved.length > 0 && (
          <section style={{ marginTop: "2.5rem" }}>
            <h2 style={{ fontSize: "1.3rem" }}>Your songs</h2>
            <div className="jp-card-grid" style={{ marginTop: "1rem" }}>
              {saved.map((s) => (
                <div key={s.id} className="jp-card">
                  <span className="jp-card-title">{s.title}</span>
                  <span className="jp-card-meta">saved on this device</span>
                  <span className="jp-card-actions">
                    <Link to={`/jianpu/play/${s.id}`}>practice</Link>
                    <Link to={`/jianpu/compose?load=${s.id}`}>edit</Link>
                    <button
                      type="button"
                      style={{
                        border: "none",
                        background: "none",
                        color: "var(--jp-ink-dim)",
                        cursor: "pointer",
                        padding: 0,
                        font: "inherit",
                      }}
                      onClick={() => removeSaved(s.id)}
                    >
                      delete
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section style={{ marginTop: "2.5rem" }}>
          <h2 style={{ fontSize: "1.3rem" }}>Tools</h2>
          <div className="jp-card-grid" style={{ marginTop: "1rem" }}>
            <Link to="/jianpu/compose" className="jp-card">
              <span className="jp-card-title">Write a song ✎</span>
              <span className="jp-card-meta">
                Type jianpu on the left, see real notation on the right — or
                record straight from the instrument and get the text written
                for you. Save, export, print.
              </span>
            </Link>
            <Link to="/jianpu/chords" className="jp-card">
              <span className="jp-card-title">Chord Lab ⌘</span>
              <span className="jp-card-meta">
                Every chord is an every-other-button shape. See them light up
                on the grid, hear them, and loop the classic progressions —
                pop, doo-wop, Creep, epic minor.
              </span>
            </Link>
          </div>
        </section>

        <section className="jp-panel" style={{ marginTop: "2.5rem", maxWidth: "38rem" }}>
          <p className="jp-help">
            <strong>Plugging in:</strong> play-along and recording use Web
            MIDI, which needs Chrome or Edge. Everything else — listening,
            reading, composing by hand — works in any browser. Set the
            transpose dial wherever you like: the app matches what you play by
            scale degree, not pitch, after a one-button calibration.
          </p>
        </section>
      </div>
    </div>
  );
};

export default JianpuStudio;
